(ns ui.view.transaction-process.pdf-util
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :refer [tr]]
   [common.schema.entities.decision-participant-options :as participant-opts]
   [frontend.state.current-session :as session]
   [oops.core :as oops]
   [ui.view.transaction-process.util :as tp-util]))

(def ^:private styles
  {:main-header    {:fontSize 14
                    :bold     true
                    :margin   [0 0 0 4]}
   :meta-line      {:fontSize 9
                    :color    "#666666"
                    :margin   [0 0 0 10]}
   :sub-header     {:fontSize 12
                    :bold     true
                    :margin   [0 18 0 6]
                    :color    "#333333"}
   :card-header    {:fontSize 10
                    :bold     true
                    :margin   [0 12 0 2]
                    :color    "#444444"}
   :entity-header  {:fontSize 10
                    :bold     true
                    :margin   [0 6 0 2]
                    :color    "#444444"}
   :label          {:fontSize 8
                    :bold     false
                    :margin   [0 3 0 0]
                    :color    "#999999"}
   :value          {:fontSize 10
                    :bold     false
                    :margin   [0 1 0 3]
                    :color    "#333333"}
   :empty-value    {:fontSize 10
                    :bold     false
                    :margin   [0 1 0 3]
                    :color    "#CCCCCC"}
   :error-value    {:fontSize 10
                    :bold     true
                    :margin   [0 1 0 3]
                    :color    "#CC0000"}
   :table-header   {:fontSize 9
                    :bold     true
                    :color    "#555555"}
   :doc-filename   {:fontSize 9
                    :color    "#555555"
                    :margin   [0 1 0 1]}})

(defn- pdf-error-text
  "Log a PDF resolution failure and return a visible error string for pdfmake."
  [{:keys [value] :as opts}]
  (tp-util/log-resolve-failure! (update opts :context #(str "[PDF] " %)))
  (str "\u26A0 " (tr :transaction-process/error-resolving-value) ": " (pr-str value)))

(defn- separator-line []
  {:canvas [{:type "line" :x1 0 :y1 0 :x2 515 :y2 0
             :lineWidth 0.5 :lineColor "#DDDDDD"}]
   :margin [0 0 0 6]})

(defn- label-value
  "Render a label + value pair. Returns a vector of two pdfmake elements."
  [label value]
  (let [error?     (and (string? value) (string/starts-with? value "\u26A0 "))
        has-value? (and (some? value) (not= value "") (not= value "-"))]
    [{:text label :style :label}
     {:text  (or value "-")
      :style (cond
               error?     :error-value
               has-value? :value
               :else      :empty-value)}]))

;; Header

(defn- build-header
  [{:keys [entity-data template current-stage]}]
  (let [display-name (or (:transaction-process/short-name entity-data)
                         (entity/display-string entity-data)
                         (tr :transaction))
        vehicle-id   (:transaction-process/vehicle entity-data)
        vehicle      (when vehicle-id (session/get-entity vehicle-id))
        vehicle-name (when vehicle (entity/display-string vehicle))
        stage-label  (some (fn [s] (when (= (:stage/id s) current-stage) (:stage/label s)))
                           (:template/stages template))
        fields-config (:template/fields template)
        inv-type-cfg (get fields-config :transaction-process/investment-type)
        inv-type-val (:transaction-process/investment-type entity-data)
        inv-type-display (when (seq inv-type-val)
                           (->> inv-type-val
                                (map (fn [v]
                                       (or (tp-util/keyword-label inv-type-cfg v)
                                           (pdf-error-text
                                            {:context "Unknown :transaction-process/investment-type option"
                                             :value v}))))
                                (string/join ", ")))
        tx-type      (:transaction-process/transaction-type entity-data)
        tx-type-display (when tx-type
                          (or (tp-util/lookup-keyword-label template :transaction-process/transaction-type tx-type)
                              (pdf-error-text
                               {:context "Unknown :transaction-process/transaction-type"
                                :value tx-type})))
        status       (:transaction-process/status entity-data)
        status-display (when status
                         (or (tp-util/lookup-keyword-label :transaction-process/status status)
                             (pdf-error-text
                              {:context "Unmapped :transaction-process/status"
                               :value status})))
        meta-parts   (cond-> []
                       vehicle-name    (conj (str (tr :vehicle) ": " vehicle-name))
                       tx-type-display (conj (str (tr :type) ": " tx-type-display))
                       inv-type-display (conj (str (tr :transaction-process/investment-type) ": " inv-type-display))
                       status-display  (conj (str (tr :status) ": " status-display))
                       stage-label     (conj (str (tr :stage) ": " stage-label)))]
    [{:text display-name :style :main-header}
     (when (seq meta-parts)
       {:text  (string/join "  \u2022  " meta-parts)
        :style :meta-line})]))

;; Property list cards

(defn- two-column-stack
  "Lay out [label value] pairs into two pdfmake columns, filling row-wise (left,
  right, left, right …) to mirror the on-screen two-column grid."
  [pairs]
  (let [col (fn [parity]
              {:width "*"
               :stack (->> pairs
                           (keep-indexed (fn [i pair] (when (= parity (mod i 2)) pair)))
                           (mapcat (fn [[label value]] (label-value label value)))
                           vec)})]
    {:columns   [(col 0) (col 1)]
     :columnGap 24}))

(defn- card-field-pairs
  "Visible, non-attachment [label value] pairs for a field card, in template order.
  short-name is dropped — it is already the document title in the header."
  [{:keys [card fields-config entity-data]}]
  (->> (:card/fields card)
       (remove #{:transaction-process/short-name})
       (remove #(tp-util/attachment-slot-field? (get fields-config %)))
       (filter #(tp-util/field-visible? {:field-config (get fields-config %)
                                         :value-lookup entity-data}))
       (mapv (fn [fkw]
               (let [fc (get fields-config fkw)]
                 [(or (:field/label fc)
                      (pdf-error-text {:context (str "Missing :field/label for " fkw)
                                       :value fkw}))
                  (tp-util/format-field-value {:field-kw    fkw
                                               :field-cfg   fc
                                               :entity-data entity-data})])))))

(defn- build-field-stage-sections
  "TP simple fields grouped by stage: a per-stage sub-header, then each field card's
  label followed by its fields in two columns — mirroring the form's stage cards.
  Attachment-slot fields are skipped (the Documents section renders them)."
  [{:keys [template entity-data]}]
  (let [fields-config (:template/fields template)]
    (->> (:template/stages template)
         (keep (fn [stage]
                 (let [card-blocks (->> (:stage/cards stage)
                                        (filter #(= (:card/type %) :field-card))
                                        (mapcat (fn [card]
                                                  (let [pairs (card-field-pairs {:card          card
                                                                                 :fields-config fields-config
                                                                                 :entity-data   entity-data})]
                                                    (when (seq pairs)
                                                      [{:text (:card/label card) :style :card-header}
                                                       (two-column-stack pairs)]))))
                                        vec)]
                   (when (seq card-blocks)
                     {:stack (into [{:text (:stage/label stage) :style :sub-header}
                                    (separator-line)]
                                   card-blocks)}))))
         vec)))

;; Documents / attachments

(defn- build-documents-section
  "Attachment slots grouped by stage: a Documents sub-header, then per stage a stage
  header followed by each populated slot's label and filenames. Grouping by stage
  disambiguates same-labelled slots like the per-stage 'Other documents'."
  [{:keys [entity-data template]}]
  (let [fields-config (:template/fields template)
        attachments   (->> (:common/attachments entity-data)
                           tp-util/resolve-refs
                           (sort-by :attachment/uploaded-on))
        stage-blocks  (->> (:template/stages template)
                           (mapcat (fn [stage]
                                     (let [slot-blocks
                                           (->> (:stage/cards stage)
                                                (filter #(= (:card/type %) :field-card))
                                                (mapcat :card/fields)
                                                (mapcat (fn [fkw]
                                                          (let [fc (get fields-config fkw)]
                                                            (when (tp-util/attachment-slot-field? fc)
                                                              (let [slot-atts (filter #(contains? (set (:common/system-kws %)) fkw)
                                                                                      attachments)]
                                                                (when (seq slot-atts)
                                                                  (into [{:text (:field/label fc) :style :label}]
                                                                        (mapv (fn [att]
                                                                                {:text  (str "\u2022 " (:attachment/filename att))
                                                                                 :style :doc-filename})
                                                                              slot-atts))))))))
                                                vec)]
                                       (when (seq slot-blocks)
                                         (into [{:text (:stage/label stage) :style :card-header}]
                                               slot-blocks)))))
                           vec)]
    (when (seq stage-blocks)
      {:stack (into [{:text (tr :documents) :style :sub-header}
                     (separator-line)]
                    stage-blocks)})))

;; Sub-entity helpers

(defn- condition-met-text
  [{:keys [status status-true status-false status-kw]}]
  (cond
    (nil? status)           "-"
    (= status status-true)  (tr :met)
    (= status status-false) (tr :not-met)
    :else                   (pdf-error-text {:context (str "Unmapped " status-kw) :value status})))

(def ^:private pdf-cell-border
  {:border [true true true true] :borderColor ["#DDD" "#DDD" "#DDD" "#DDD"]})

(defn- build-conditions-table
  "`show-status?` adds the met/not-met column. Decision conditions are
  status-less (CapMan), so the decisions section passes false; verification
  (attestation) conditions keep their status column."
  [conditions-refs {:keys [show-status?]}]
  (let [conditions (->> (tp-util/resolve-refs conditions-refs)
                        (sort-by #(or (:common/ord %) 0)))]
    (when (seq conditions)
      {:margin [0 4 0 4]
       :table
       (if show-status?
         {:widths     ["*" 80]
          :headerRows 1
          :body
          (into
           [[(merge pdf-cell-border {:text (tr :condition) :style :table-header :fillColor "#F5F5F5"})
             (merge pdf-cell-border {:text (tr :status)    :style :table-header :fillColor "#F5F5F5"})]]
           (->> conditions
                (mapv (fn [c]
                        (let [{:keys [title-kw status-kw] :as attrs} (tp-util/condition-attrs c)
                              status      (get c status-kw)
                              status-text (condition-met-text (assoc attrs :status status))]
                          [(merge pdf-cell-border {:text (or (get c title-kw) "-") :style :value})
                           (merge pdf-cell-border
                                  {:text  status-text
                                   :style (if (string/starts-with? status-text "\u26A0 ") :error-value :value)})])))))}
         {:widths     ["*"]
          :headerRows 1
          :body
          (into
           [[(merge pdf-cell-border {:text (tr :condition) :style :table-header :fillColor "#F5F5F5"})]]
           (->> conditions
                (mapv (fn [c]
                        (let [{:keys [title-kw]} (tp-util/condition-attrs c)]
                          [(merge pdf-cell-border {:text (or (get c title-kw) "-") :style :value})])))))})})))

(defn- participant-display-name
  "Resolve the display name for a decision-participant entity.
   Follows the :decision-participant/person ref to the actual person/account."
  [participant]
  (let [person-ref (:decision-participant/person participant)
        person-id  (when person-ref (or (:db/id person-ref) person-ref))
        person     (when person-id (session/get-entity person-id))]
    (when person
      (entity/display-string person))))

(defn- resolve-ref-multi-labels
  "Resolve a ref-multi field (e.g. named-value refs) into a seq of display strings."
  [entity field-kw]
  (let [refs (get entity field-kw)]
    (when (seq refs)
      (->> refs
           (map (fn [ref]
                  (let [eid (or (:db/id ref) ref)
                        resolved (session/get-entity eid)]
                    (when resolved (entity/display-string resolved)))))
           (remove nil?)
           seq))))

(defn- participant-detail-text
  "Build a display string for a participant: name + attendance + role + vote."
  [participant]
  (let [person-name (or (participant-display-name participant) "-")
        attendance  (:decision-participant/attendance participant)
        att-label   (when attendance (participant-opts/get-label attendance))
        vote        (:decision-participant/vote participant)
        vote-label  (when vote (participant-opts/get-label vote))
        role-names  (resolve-ref-multi-labels participant :decision-participant/role)
        detail-parts (cond-> []
                       att-label  (conj att-label)
                       role-names (conj (string/join ", " role-names))
                       vote-label (conj vote-label))]
    (if (seq detail-parts)
      (str person-name " (" (string/join ", " detail-parts) ")")
      person-name)))

(defn- build-participants-list
  [participants-refs]
  (let [participants (->> (tp-util/resolve-refs participants-refs)
                          (sort-by #(or (:common/ord %) 0)))]
    (when (seq participants)
      {:stack
       (into [{:text (tr :participants) :style :label}]
             (->> participants
                  (mapv (fn [p]
                          {:text  (participant-detail-text p)
                           :style :value}))))})))

(defn- reservation-type-title
  [res]
  (let [type-ref  (:investment-reservation/type res)
        type-id   (or (:db/id type-ref) type-ref)
        type-ent  (when type-id (session/get-entity type-id))]
    (or (:investment-reservation-type/title type-ent)
        (when type-ent (entity/display-string type-ent)))))

(defn- reservation-field-kws
  "The reservation fields to show, in order, taken from the template's
   `:template/reservation-layout` (the same arrangement the on-screen decision
   modal uses). Flattened to a plain ordered list — the PDF stacks fields
   vertically, so the nested row/column grouping is irrelevant here."
  [template]
  (vec (flatten (:template/reservation-layout template))))

(defn- reservation-rows
  "Return a vec of pdfmake elements for a single reservation — a type header (if any)
   followed by label/value pairs for each populated field. Field list comes from
   the template; labels resolve from the schema via `tp-util/field-label`."
  [{:keys [template reservation id->entity]}]
  (let [res        reservation
        type-title (reservation-type-title res)
        field-rows (->> (reservation-field-kws template)
                        (mapcat (fn [field-kw]
                                  (when-let [v (get res field-kw)]
                                    (when-not (and (string? v) (string/blank? v))
                                      (label-value (tp-util/field-label
                                                    field-kw
                                                    (get-in template [:template/fields field-kw]))
                                                   (tp-util/materialize-field
                                                    {:field      field-kw
                                                     :entity     res
                                                     :id->entity id->entity})))))))]
    (vec (concat (when type-title [{:text type-title :style :card-header}])
                 field-rows))))

(defn- build-reservations-list
  [{:keys [template reservations-refs id->entity]}]
  (let [reservations (->> (tp-util/resolve-refs reservations-refs)
                          (sort-by #(or (:common/ord %) 0)))]
    (when (seq reservations)
      {:stack
       (into [{:text (tr :transaction-process/investment-reservations) :style :label}]
             (->> reservations
                  (mapv (fn [res]
                          {:margin [0 2 0 4]
                           :stack  (reservation-rows
                                    {:template    template
                                     :reservation res
                                     :id->entity  id->entity})}))))})))

(defn- build-sub-entity-attachments
  "Render attachment filenames for a sub-entity (decision, attestation)."
  [entity]
  (let [att-refs (:common/attachments entity)
        attachments (->> att-refs
                         (map (fn [ref]
                                (let [eid (or (:db/id ref) ref)]
                                  (session/get-entity eid))))
                         (remove nil?)
                         (remove :common/disabled?))]
    (when (seq attachments)
      {:stack
       (into [{:text (tr :documents) :style :label}]
             (->> attachments
                  (mapv (fn [att]
                          {:text  (str "\u2022 " (:attachment/filename att))
                           :style :doc-filename}))))})))

;; Decisions

(defn- slot-value-rows
  "Flat seq of label-value PDF elements for a sub-entity's display fields, derived
  from the slot's `slot-display-fields` — the single field list also driving the
  peek panel and details modal, so the three read surfaces can't drift. Date fields
  always render; other fields render when present. Values format through the same
  `format-field-value` the on-screen views use."
  [{:keys [slot-template category entity template]}]
  (let [field-configs (:template/fields template)]
    (->> (tp-util/slot-display-fields {:slot-template slot-template :category category})
         (mapcat (fn [fk]
                   (let [fc        (get field-configs fk)
                         data-type (:field/data-type fc)]
                     (when (or (some? (get entity fk)) (= data-type :data.type/date))
                       (label-value (tp-util/field-label fk fc)
                                    (tp-util/format-field-value
                                     {:field-kw fk :field-cfg fc :entity-data entity})))))))))

(defn- build-decision
  [{:keys [decision entry template id->entity]}]
  (let [type-label (or (:slot/label entry)
                       (pdf-error-text {:context "Missing :slot/label"
                                        :value entry}))]
    {:margin [0 4 0 4]
     :stack
     (if-not decision
       (into [{:text type-label :style :entity-header}
              (separator-line)]
             (label-value (tr :status) (tr :pending)))
       (let [dec decision
             slot-template (:slot/template entry)
             draft? (:decision/draft? dec)]
         (cond-> [{:text type-label :style :entity-header}
                  (separator-line)]

           true
           (into (label-value (tr :status) (if draft? (tr :draft) (tr :logged))))

           true
           (into (slot-value-rows {:slot-template slot-template :category :decisions
                                   :entity dec :template template}))

           (seq (:decision/participants dec))
           (conj (build-participants-list (:decision/participants dec)))

           (seq (:decision/investment-reservations dec))
           (conj (build-reservations-list
                  {:template          template
                   :reservations-refs (:decision/investment-reservations dec)
                   :id->entity        id->entity}))

           (seq (:decision/conditions dec))
           (conj (build-conditions-table (:decision/conditions dec) {:show-status? false}))

           (seq (:common/attachments dec))
           (conj (build-sub-entity-attachments dec)))))}))

(defn- build-decisions-section
  [{:keys [entity-data template id->entity]}]
  (let [slots    (tp-util/displayable-slots-by-category template entity-data :decisions)
        sections (mapv (fn [{:keys [entry instance]}]
                         (build-decision {:decision   instance
                                          :entry      entry
                                          :template   template
                                          :id->entity id->entity}))
                       slots)]
    (when (seq sections)
      {:stack (into [{:text (tr :decisions) :style :sub-header}] sections)})))

;; Attestations

(defn- build-attestation
  [{:keys [attestation entry template]}]
  (let [type-label (or (:slot/label entry)
                       (pdf-error-text {:context "Missing :slot/label"
                                        :value entry}))]
    {:margin [0 4 0 4]
     :stack
     (if-not attestation
       (into [{:text type-label :style :entity-header}
              (separator-line)]
             (label-value (tr :status) (tr :not-started)))
       (let [att attestation
             slot-template (:slot/template entry)
             draft? (:attestation/draft? att)
             outcome (:attestation/outcome att)
             ;; Per-slot outcome resolution (CapMan: CoI 4-option, Yes/No, Received/
             ;; Not received) reads the slot's per-slot outcome options.
             outcome-text (cond
                            (nil? outcome) nil
                            (tp-util/attestation-display-label entry att)
                            (tp-util/attestation-display-label entry att)
                            :else (pdf-error-text {:context "Unmapped :attestation/outcome"
                                                   :value outcome}))]
         (cond-> [{:text type-label :style :entity-header}
                  (separator-line)]

           draft?
           (into (label-value (tr :status) (tr :draft)))

           outcome-text
           (into (label-value (tr :outcome) outcome-text))

           ;; Date + comment rows come from the slot template (the Lux slot drops its
           ;; hidden date).
           true
           (into (slot-value-rows {:slot-template slot-template :category :attestations
                                   :entity att :template template}))

           (seq (:attestation/conditions att))
           (conj (build-conditions-table (:attestation/conditions att) {:show-status? true}))

           (seq (:common/attachments att))
           (conj (build-sub-entity-attachments att)))))}))

(defn- build-attestations-section
  [{:keys [entity-data template]}]
  (let [slots    (tp-util/displayable-slots-by-category template entity-data :attestations)
        sections (mapv (fn [{:keys [entry instance]}]
                         (build-attestation {:attestation instance :entry entry :template template}))
                       slots)]
    (when (seq sections)
      {:stack (into [{:text (tr :verifications) :style :sub-header}] sections)})))

;; Declarations

(defn- build-declaration
  [{:keys [declaration entry template]}]
  (let [type-label (or (:slot/label entry)
                       (pdf-error-text {:context "Missing :slot/label"
                                        :value entry}))]
    {:margin [0 4 0 4]
     :stack
     (if-not declaration
       (into [{:text type-label :style :entity-header}
              (separator-line)]
             (label-value (tr :status) (tr :pending)))
       (let [dcl declaration
             slot-template (:slot/template entry)
             status-display (cond
                              (:declaration/draft? dcl)           (tr :draft)
                              (:declaration/not-applicable? dcl)  (tr :not-applicable)
                              (:declaration/completed? dcl)       (tr :completed)
                              :else                               (tr :pending))]
         (cond-> [{:text type-label :style :entity-header}
                  (separator-line)]

           true
           (into (label-value (tr :status) status-display))

           true
           (into (slot-value-rows {:slot-template slot-template :category :declarations
                                   :entity dcl :template template})))))}))

(defn- build-declarations-section
  [{:keys [entity-data template]}]
  (let [slots    (tp-util/displayable-slots-by-category template entity-data :declarations)
        sections (mapv (fn [{:keys [entry instance]}]
                         (build-declaration {:declaration instance :entry entry :template template}))
                       slots)]
    (when (seq sections)
      {:stack (into [{:text (tr :declarations) :style :sub-header}] sections)})))

;; Main

(defn- build-doc-definition
  [{:keys [entity-data template current-stage id->entity]}]
  (let [header-elements (build-header {:entity-data   entity-data
                                       :template      template
                                       :current-stage current-stage})
        field-sections (build-field-stage-sections {:template template :entity-data entity-data})
        documents-section (build-documents-section {:entity-data entity-data :template template})
        decisions-section (build-decisions-section {:entity-data entity-data
                                                    :template    template
                                                    :id->entity  id->entity})
        attestations-section (build-attestations-section {:entity-data entity-data :template template})
        declarations-section (build-declarations-section {:entity-data entity-data :template template})
        content (cond-> (vec header-elements)
                  true (into field-sections)
                  documents-section (conj documents-section)
                  attestations-section (conj attestations-section)
                  decisions-section (conj decisions-section)
                  declarations-section (conj declarations-section))]
    {:content      [{:stack content}]
     :styles       styles
     :defaultStyle {:fontSize 10}
     :pageMargins  [40 40 40 40]}))

(defn create-and-open-pdf
  [{:keys [entity-data template current-stage id->entity]}]
  (let [doc-definition (build-doc-definition {:entity-data   entity-data
                                              :template      template
                                              :current-stage current-stage
                                              :id->entity    id->entity})
        pdf-doc (oops/ocall js/pdfMake "createPdf" (clj->js doc-definition))]
    (oops/ocall pdf-doc "open")))
